import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Icon } from '@fluentui/react/lib/Icon';
import {
  ContextualMenu, ContextualMenuItemType, DirectionalHint, IContextualMenuItem, Stack,
} from '@fluentui/react';
import { Link } from '@fluentui/react/lib/Link';
import { mergeStyleSets, getTheme, keyframes } from '@fluentui/react/lib/Styling';
import { FontSizes } from '@uifabric/fluent-theme/lib/fluent/FluentType';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { createAvatar } from '@dicebear/core';
import * as initials from '@dicebear/initials';
import { Container } from '../layout';
import { useAppSelector } from '../../redux/hooks';
import { getPermissions } from '../../redux/permission/permissionSlice';
import BillitAssignedInvoicesHeaderNotice from './BillitAssignedInvoicesHeaderNotice';

const redirectUri = process.env.REACT_APP_REDIRECT_URI || '';
const clientId = process.env.REACT_APP_CLIENT_ID || '';

const theme = getTheme();

// zachte "ademhaling" van het meldingsbolletje: lichte gloed die uitdooft
const softPulse = keyframes({
  '0%': { opacity: 1, boxShadow: '0 0 0 0 rgba(109, 31, 128, 0.45)' },
  '60%': { opacity: 0.75, boxShadow: '0 0 0 5px rgba(109, 31, 128, 0)' },
  '100%': { opacity: 1, boxShadow: '0 0 0 0 rgba(109, 31, 128, 0)' },
});

// paars van het 3bouw-logo
const brandPurple = '#6d1f80';
const logoSrc = `${process.env.PUBLIC_URL}/3bouw-logo-baseline-wit-scaled.webp`;

// const filterIcon = { iconName: 'Filter' };
const classes = mergeStyleSets({
  header: {
    background: theme.palette.themePrimary,
    color: 'white',
    // vaste hoogte: vroeger bepaalden de marges van de titel "Bouw Manager" de hoogte
    minHeight: 60,
  },
  icon: {},
  navItem: {
    selectors: {
      ':hover': {
        background: theme.palette.themeTertiary,
      },
    },
    display: 'flex',
    alignItems: 'center',
  },
  innerItem: {
    display: 'flex',
    paddingTop: theme.spacing.s2,
    paddingBottom: theme.spacing.s2,
    paddingLeft: theme.spacing.m,
    paddingRight: theme.spacing.m,
    fontSize: FontSizes.size14,
    lineHeight: FontSizes.size14,
    color: 'white',
    selectors: {
      ':hover': {
        textDecoration: 'none',
      },
    },
    alignItems: 'center',
  },
  // Rechterkant: gewone witte tekst, elk onderdeel over de volle hoogte met dezelfde
  // hover als de menuknop, gescheiden door dunne lijntjes. Geen pillen, cirkels of badges.
  barItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: '100%',
    padding: `0 ${theme.spacing.m}`,
    color: 'white',
    fontSize: FontSizes.size14,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    cursor: 'default',
  },
  barLink: {
    cursor: 'pointer',
    selectors: {
      ':hover': { background: theme.palette.themeTertiary, color: 'white', textDecoration: 'none' },
      ':focus-visible': { outline: '1px solid white', outlineOffset: -3 },
    },
  },
  separator: {
    width: 1,
    alignSelf: 'center',
    height: 24,
    background: 'rgba(255, 255, 255, 0.45)',
  },
  // datum als klein kalenderblaadje (maand + dag) met weekdag en week ernaast
  date: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: 'white',
    whiteSpace: 'nowrap',
    lineHeight: '1.15',
  },
  sheet: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // even groot als de avatar (30 x 30)
    width: 30,
    height: 30,
    boxSizing: 'border-box',
    borderRadius: 5,
    overflow: 'hidden',
    background: 'white',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
  },
  sheetMonth: {
    width: '100%',
    textAlign: 'center',
    background: brandPurple,
    color: 'white',
    fontSize: 7,
    lineHeight: '10px',
    height: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  sheetDay: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    color: brandPurple,
    fontSize: 13,
    lineHeight: '20px',
    fontWeight: 700,
  },
  dateText: { display: 'flex', flexDirection: 'column' },
  weekday: { fontSize: FontSizes.size14, fontWeight: 600 },
  week: { fontSize: FontSizes.size12, opacity: 0.85 },
  // factuurmelding: halfvet, met een klein paars stipje op het belletje
  notice: { fontWeight: 600 },
  noticeDot: {
    position: 'absolute',
    top: -2,
    right: -3,
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: brandPurple,
    animationName: softPulse,
    animationDuration: '2.4s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    selectors: {
      '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
    },
  },
  logo: { height: 30, width: 'auto', display: 'block' },
  // avatar (DiceBear 'initials'): wit met paarse initialen
  avatar: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    display: 'block',
    flexShrink: 0,
  },
  chevron: { fontSize: 10, opacity: 0.9 },
  hideNarrow: { selectors: { '@media (max-width: 900px)': { display: 'none' } } },
  searchBox: {
    marginTop: theme.spacing.s2,
    marginBottom: theme.spacing.s2,
  },
  searchContainer: {
    maxWidth: '750px',
  },
});

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** Datum van vandaag; wisselt vanzelf om middernacht (controle elke minuut). */
const TodayLabel = () => {
  const [today, setToday] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = new Date();
      setToday(previous => (previous.toDateString() === now.toDateString() ? previous : now));
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const long = capitalize(today.toLocaleDateString('nl-BE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }));
  const weekday = capitalize(today.toLocaleDateString('nl-BE', { weekday: 'long' }));
  const month = today.toLocaleDateString('nl-BE', { month: 'short' }).replace('.', '');
  const week = moment(today).isoWeek();

  return (
    <span className={classes.date} title={`${long}, week ${week}`}>
      <span className={classes.sheet} aria-hidden>
        <span className={classes.sheetMonth}>{month}</span>
        <span className={classes.sheetDay}>{today.getDate()}</span>
      </span>
      <span className={classes.dateText}>
        <span className={classes.weekday}>{weekday}</span>
        <span className={classes.week}>{`week ${week}`}</span>
      </span>
    </span>
  );
};

/** Avatar met initialen (DiceBear, lokaal als SVG gegenereerd): wit met paarse letters. */
const useAvatar = (name: string) => useMemo(() => createAvatar(initials, {
  seed: name || '?',
  backgroundColor: ['ffffff'],
  textColor: [brandPurple.slice(1)],
  fontWeight: 600,
  fontSize: 40,
  size: 30,
}).toDataUri(), [name]);

/** Avatar + naam met een uitklapmenu (snelkoppelingen en afmelden). */
const UserMenu = ({
 name, email, onNavigate, onLogout,
}: {
  name: string;
  email?: string;
  onNavigate?: (path: string) => void;
  onLogout: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const target = useRef<HTMLButtonElement>(null);
  const avatar = useAvatar(name);
  const permissions = useAppSelector(getPermissions);
  const go = (path: string) => (onNavigate ? onNavigate(path) : window.location.assign(path));

  const items: IContextualMenuItem[] = [
    {
      key: 'who',
      itemType: ContextualMenuItemType.Header,
      text: email ? `${name} · ${email}` : name,
    },
    ...(permissions.includes('nav:incomingInvoices') ? [{
      key: 'assigned', text: 'Mijn toegewezen facturen', iconProps: { iconName: 'Ringer' }, onClick: () => go('/billit-invoices-assigned'),
    }] : []),
    ...(permissions.includes('nav:suggestedValueCategories') ? [{
      key: 'settings', text: 'Instellingen', iconProps: { iconName: 'Settings' }, onClick: () => go('/settings'),
    }] : []),
    { key: 'divider', itemType: ContextualMenuItemType.Divider },
    {
      key: 'logout', text: 'Afmelden', iconProps: { iconName: 'SignOut' }, onClick: onLogout,
    },
  ];

  return (
    <>
      <button
        ref={target}
        type='button'
        className={`${classes.barItem} ${classes.barLink}`}
        onClick={() => setOpen(!open)}
        aria-haspopup='menu'
        aria-expanded={open}
        title={name}
      >
        <img src={avatar} alt='' className={classes.avatar} />
        <span className={classes.hideNarrow}>{name}</span>
        <Icon iconName='ChevronDown' className={classes.chevron} />
      </button>
      {open && (
        <ContextualMenu
          target={target}
          items={items}
          directionalHint={DirectionalHint.bottomRightEdge}
          onDismiss={() => setOpen(false)}
          onItemClick={() => setOpen(false)}
        />
      )}
    </>
  );
};

const Header: React.FC = ({ children }) => (
  <Container
    horizontal
    horizontalAlign='space-between'
    fullWidth
    className={classes.header}
  >
    {children}
  </Container>
);

const InnerHeader = ({
  toggleNavigation = () => {},
  onNavigate,
}: { toggleNavigation?: () => void; onNavigate?: (path: string) => void }) => {
  const { isAuthenticated, logout, user } = useAuth0();

  const getUserName = () => {
    if (!isAuthenticated || !user) return '';
    if (user.name && user.family_name) return `${user.name} ${user.family_name}`;
    return user.name || user.family_name || user.nickname || '';
  };

  return (
    <>
      <Stack.Item align='stretch'>
        <Stack horizontal disableShrink verticalFill>
          <Stack.Item className={classes.navItem} align='stretch'>
            <Link className={classes.innerItem} onClick={toggleNavigation}>
              <Icon iconName='GlobalNavButton' className={classes.icon} />
            </Link>
          </Stack.Item>
          <Stack.Item className={classes.navItem} align='stretch'>
            <Link className={classes.innerItem}>
              <img src={logoSrc} alt='3bouw' className={classes.logo} />
            </Link>
          </Stack.Item>
        </Stack>
      </Stack.Item>

      {/* <Stack.Item align="baseline" grow className={classes.searchContainer}>
        <SearchBox
          placeholder="Zoek naar content in de volledige applicatie"
          className={classes.searchBox}
          iconProps={filterIcon}
        />
      </Stack.Item>       */}
      <Stack.Item align='stretch'>
        {isAuthenticated && (
          <Stack horizontal disableShrink verticalFill verticalAlign='center'>
            <BillitAssignedInvoicesHeaderNotice
              className={`${classes.barItem} ${classes.barLink} ${classes.notice}`}
              dotClassName={classes.noticeDot}
              separatorClassName={classes.separator}
            />
            <span className={classes.barItem}>
              <TodayLabel />
            </span>
            <span className={classes.separator} />
            <UserMenu
              name={getUserName()}
              email={user?.email}
              onNavigate={onNavigate}
              onLogout={() => logout({ returnTo: redirectUri, client_id: clientId })}
            />
          </Stack>
        )}
      </Stack.Item>
    </>
  );
};

export default Header;

export { InnerHeader };
